/// <amd-module name="Wit/QueriesHub/Charts/QueryCharts" />

import * as React from "react";
import { ChartConfiguration } from "Tfs/Charting/Contracts/Reporting";
import { getReportingClient } from "Tfs/Charting/RestClient/Reporting";
import { getTFSProject } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IPerformanceScenarioPendingWork, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { traceException } from "VSS/Platform/Trace";
import { Dialog } from "VSSUI/Dialog";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { QueryHierarchyItem, QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IQueryService } from "Wit/Common/QueryService/IQueryService";
import * as QueriesHubConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import * as ChartCommon from "Wit/QueriesHub/Charts/Common/ChartCommon";
import { ChartMenuAction, ChartWrapper, IChartWrapperProps } from "Wit/QueriesHub/Charts/Components/Charts/ChartWrapper";
import { ConfigureChartPanel } from "Wit/QueriesHub/Charts/Components/Configuration/ConfigureChartPanel";
import { QueryHeader } from "Wit/QueriesHub/Common/Components/QueryHeader";
import { IQueryChartService } from "Wit/QueriesHub/Common/Services/IQueryChartService";
import * as Resources from "Wit/QueriesHub/Resources";
import { AddToDashboardPanel, WidgetData } from "../Common/Components/AddToDashboardPanel";

interface IChartsComponentState {
    chartConfigs: ChartConfiguration[] | undefined;
    chartData: (ChartCommon.ITransformQueryData | undefined)[] | undefined;
    suppressAnimation: boolean;
    queryType: QueryType | undefined;
}

interface IChartsComponentProps{
    telemetryProps: {
        parentPageName: string
    }
}

const WitChartContribution = "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.WitChartWidget";

/**
 * Displays the Charts view in the queries hub.
 */
export class ChartsComponent extends VssComponent<IChartsComponentProps, IChartsComponentState> {
    private _projectId: string;
    private _queryId: string;
    private _queryItem: QueryHierarchyItem | undefined;
    private _confirmDeleteChart = new ObservableValue<ChartConfiguration | undefined>(undefined);
    private _addToDashboardChart = new ObservableValue<ChartConfiguration | undefined>(undefined);
    private _error = new ObservableValue<string | undefined>(undefined);
    private _pendingWork: IPerformanceScenarioPendingWork | undefined;
    private _queryService: IQueryService;

    public constructor(props: IChartsComponentProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            chartConfigs: undefined,
            chartData: undefined,
            suppressAnimation: false,
            queryType: undefined
        };
        
        const project = getTFSProject(this.context.pageContext)!;
        this._projectId = project.id;
        
        this._queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
    }

    private async _loadData() {
        const state: Partial<IChartsComponentState> = {};

        try {
            this._queryId = this._queryService.getQueryId();

            const reportingClient = getReportingClient(this.context.pageContext);
            const chartConfigs = (await reportingClient.getChartConfigurations(this._projectId, ChartCommon.scope, this._queryId)).map(
                ccr => ccr.chartConfiguration
            );
            if (this._queryId) {
                this._queryItem = await this._queryService.getQuery(this._projectId);
            }

            if (this._queryItem?.queryType === QueryType.Flat && !this._queryService.isEditorDirty.value) {
                state["chartConfigs"] = chartConfigs;

                if (chartConfigs.length > 0 ) {
                    const queryChartService = this.context.pageContext.getService<IQueryChartService>("IQueryChartService");
                    const chartData = await queryChartService.getChartData(this._projectId, chartConfigs);

                    state["chartData"] = chartData.result;
                    state["suppressAnimation"] = false;
                }
            }

            const queryType = this._queryService.isEditorDirty.value
                // Actual type of query (when have switched to another type, but have not yet saved it)
                ? this._queryService.getQueryType(this._queryService.editInfo.value) 
                 // Saved type of query
                : this._queryItem?.queryType;

            if (queryType && queryType !== this.state.queryType) {
                state["queryType"] = queryType;
            }
        } catch (err) {
            traceException(this.context.pageContext, "WIT", "Charts", err);
            this._error.value = err instanceof Error ? err.message : "" + err;
        }

        if (Object.keys(state).length) {
            this.setState({ ...state as IChartsComponentState });
        }
    }

    public async componentDidMount() {
        if (!this._queryService.isNewQuery && !this._queryService.tempQueryId && !this._queryService.urlWiql ) {
            await this._loadData();
        }

        this._pendingWork?.complete();
    }

    public render(): React.ReactNode {
        let content: React.ReactNode;
        const { chartConfigs } = this.state;

        if (this.state.queryType && this.state.queryType !== QueryType.Flat) {
            content = (
                <ZeroData
                    imageAltText={Resources.UnsavedChartPrimary}
                    imagePath={getAssetLocation(
                        this.context.pageContext,
                        "ms.vss-work-web/new-queries-hub-content/Illustrations/zerodata-charting-not-supported.svg"
                    )}
                    primaryText={Resources.QueryTypeErrorPrimaryText}
                    secondaryText={Resources.QueryTypeErrorSecondaryText}
                />
            )
        } else if (
            this._queryService.isNewQuery
            || this._queryService.tempQueryId
            || this._queryService.urlWiql
            || (this.state.queryType === QueryType.Flat && this._queryService.isEditorDirty.value)
        ) {
            content = (
                <ZeroData
                    imageAltText={Resources.UnsavedChartPrimary}
                    imagePath={getAssetLocation(
                        this.context.pageContext,
                        "ms.vss-work-web/new-queries-hub-content/Illustrations/zerodata-no-charts.svg"
                    )}
                    primaryText={Resources.UnsavedChartPrimary}
                    secondaryText={Resources.UnsavedChartSecondary}
                />
            );
        } else if (chartConfigs && chartConfigs.length > 0) {
            content = (
                <div className="flex-row flex-wrap">
                    {chartConfigs.map((cc, i) => (
                        <ChartWrapper
                            key={cc.chartId}
                            chartConfiguration={cc}
                            className="subtle-border margin-right-16"
                            results={this.state.chartData && this.state.chartData[i]}
                            suppressAnimation={this.state.suppressAnimation}
                            onAction={this._onChartAction}
                            isPublic={this._queryItem?.isPublic}
                        />
                    ))}
                </div>
            );
        } else if (chartConfigs) {
            content = (
                <ZeroData
                    actionText={Resources.NewChart}
                    actionType={ZeroDataActionType.ctaButton}
                    imagePath={getAssetLocation(
                        this.context.pageContext,
                        "ms.vss-work-web/new-queries-hub-content/Illustrations/zerodata-no-charts.svg"
                    )}
                    imageAltText={Resources.ChartsZeroDataImageAltText}
                    primaryText={Resources.ChartsZeroDataPrimary}
                    secondaryText={Resources.ChartsZeroDataSecondary}
                    onActionClick={this._onAddChart}
                />
            );
        } else {
            content = <Spinner size={SpinnerSize.large} />;
        }

        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    if (!this._pendingWork) {
                        this._pendingWork = scenarioProps.registerPendingWork(QueriesHubConstants.ChartsTabViewed);
                    }

                    return (
                        <Surface background={SurfaceBackground.neutral}>
                            <Page className="query-charts-page flex-grow flex-column ">
                                <QueryHeader
                                    selectedTabId={"charts"}
                                    onRunQuery={this._onRunQuery}
                                    onAddChart={this._onAddChart}
                                    queryType={this.state.queryType}
                                />
                                <Observer error={this._error}>
                                    {({ error }: { error: string | undefined }) =>
                                        error ? (
                                            <MessageCard severity={MessageCardSeverity.Error} onDismiss={() => (this._error.value = undefined)}>
                                                {error}
                                            </MessageCard>
                                        ) : null
                                    }
                                </Observer>
                                <div className="query-charts-content">{content}</div>
                                <Observer confirmDeleteChart={this._confirmDeleteChart}>
                                    {({ confirmDeleteChart }: { confirmDeleteChart: ChartConfiguration | undefined }) => {
                                        return confirmDeleteChart ? (
                                            <Dialog
                                                titleProps={{ text: Resources.ConfirmTitle }}
                                                footerButtonProps={[
                                                    { text: Resources.Cancel, onClick: () => this._onDeleteDismiss() },
                                                    { primary: true, text: Resources.DeleteChart, onClick: () => this._onDeleteDismiss(true) }
                                                ]}
                                                onDismiss={this._onDeleteDismiss}
                                            >
                                                {format(Resources.DeleteChartConfirmation, confirmDeleteChart.title)}
                                            </Dialog>
                                        ) : null;
                                    }}
                                </Observer>
                                <Observer addToDashboardChart={this._addToDashboardChart}>
                                    {({ addToDashboardChart }: { addToDashboardChart: ChartConfiguration | undefined }) =>
                                        addToDashboardChart ? (
                                            <AddToDashboardPanel
                                                onDismiss={() => (this._addToDashboardChart.value = undefined)}
                                                projectId={this._projectId}
                                                widgetOptions={this._createWidgetProps(addToDashboardChart)}
                                                telemetryProps={this.props.telemetryProps}
                                            />
                                        ) : null
                                    }
                                </Observer>
                            </Page>
                        </Surface>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }

    private _createWidgetProps(addToDashboardChart: ChartConfiguration): WidgetData {
        const widgetColumnSpan = 2;
        const widgetRowSpan = 2;

        return {
            name: addToDashboardChart.title,
            contributionId: WitChartContribution,
            settings: JSON.stringify({
                chartId: addToDashboardChart.chartId
            }),
            size: { columnSpan: widgetColumnSpan, rowSpan: widgetRowSpan }
        };
    }

    private _onDeleteDismiss = async (deleteChart?: boolean) => {
        const chartConfig = this._confirmDeleteChart.value;
        this._confirmDeleteChart.value = undefined;

        if (deleteChart && chartConfig) {
            try {
                const reportingClient = getReportingClient(this.context.pageContext);
                await reportingClient.deleteChartConfiguration(this._projectId, chartConfig.chartId);
                const idx = this.state.chartConfigs?.findIndex(c => c.chartId === chartConfig.chartId);
                if (idx !== undefined && idx >= 0) {
                    const chartConfig = [...(this.state.chartConfigs ?? [])];
                    chartConfig.splice(idx, 1);
                    const chartData = [...(this.state.chartData ?? [])];
                    chartData.splice(idx, 1);
                    this.setState({
                        chartConfigs: chartConfig,
                        chartData,
                        suppressAnimation: true
                    });
                }
            } catch (err) {
                this._error.value = `${Resources.DeleteChartError} ${err instanceof Error ? err.message : err}`;
            }
        }
    };

    private async _updateSingleChart(config: ChartConfiguration) {
        const queryChartService = this.context.pageContext.getService<IQueryChartService>("IQueryChartService");
        let idx = this.state.chartConfigs?.findIndex(c => c.chartId === config.chartId);
        const chartConfigs = [...(this.state.chartConfigs ?? [])];
        let chartData = [...(this.state.chartData ?? [])];

        if (idx !== undefined && idx >= 0) {
            // update existing chart
            chartConfigs[idx] = config;
            chartData[idx] = undefined;
        } else {
            // add new chart
            idx = chartConfigs.length;
            chartConfigs.push(config);
            chartData.push(undefined);
        }
        this.setState({ chartConfigs, chartData, suppressAnimation: true });
        const data = await queryChartService.getChartData(this._projectId, [config]);
        chartData = [...(this.state.chartData || [])];
        chartData[idx] = data.result[0];
        this.setState({ chartData, suppressAnimation: true });
    }

    private _onChartAction: IChartWrapperProps["onAction"] = (chartId, action) => {
        const chartConfig = this.state.chartConfigs?.find(c => c.chartId === chartId);
        if (!chartConfig) {
            return;
        }

        switch (action) {
            case ChartMenuAction.addToDashboard:
                this._addToDashboardChart.value = chartConfig;
                break;
            case ChartMenuAction.edit:
                this._showConfigureChartPanel(chartConfig);
                break;
            case ChartMenuAction.delete:
                this._confirmDeleteChart.value = chartConfig;
                break;
        }
    };

    private _onRunQuery = () => {
        this.setState({ chartConfigs: undefined, chartData: undefined });
        this._loadData();
    };

    private _onAddChart = () => {
        this._showConfigureChartPanel();
    };

    private _showConfigureChartPanel = (chartConfig?: ChartConfiguration) => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => (
            <ConfigureChartPanel
                existingConfiguration={chartConfig}
                projectId={this._projectId}
                queryId={this._queryId}
                onDismiss={config => {
                    config && this._updateSingleChart(config);
                    onDismiss();
                }}
            />
        ));
    };
}
Components.add("query-charts-component", ChartsComponent);
